import React, { createContext, useContext, useState, useEffect } from 'react';
import { StoreSettings } from '../types';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface StoreSettingsContextType {
  storeSettings: StoreSettings;
  updateStoreSettings: (newSettings: Partial<StoreSettings>) => Promise<void>;
  uploadLogo: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
  isSettingsModalOpen: boolean;
  openSettingsModal: () => void;
  closeSettingsModal: () => void;
  isSaving: boolean;
}

const DEFAULT_STORE_SETTINGS: StoreSettings = {
  storeName: 'Ótica Diana',
  attendantName: 'Diana',
  cnpj: '12.345.678/0001-90',
  phone: '(11) 98765-4321',
  email: 'contato@oticadiana.com.br',
  addressStreet: 'Rua das Flores',
  addressNumber: '150',
  addressComplement: 'Sala 02',
  addressNeighborhood: 'Centro Comercial',
  addressCity: 'São Paulo',
  addressState: 'SP',
  addressZipCode: '01001-000',
  logoUrl: '',
  privacyPin: '1234',
};

const STORAGE_KEY = 'optisaas_store_settings';

const StoreSettingsContext = createContext<StoreSettingsContextType | undefined>(undefined);

export const StoreSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [storeSettings, setStoreSettings] = useState<StoreSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_STORE_SETTINGS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Erro ao carregar configurações da loja do cache local:', e);
    }
    return DEFAULT_STORE_SETTINGS;
  });

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Carrega configurações reais do Supabase quando o tenant estiver autenticado
  useEffect(() => {
    const fetchSupabaseSettings = async () => {
      if (!tenant?.id || tenant.id === 'demo-tenant-id') return;

      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('name, cnpj, attendant_name, phone, email, address_street, address_number, address_complement, address_neighborhood, address_city, address_state, address_zip_code, logo_url, privacy_pin')
          .eq('id', tenant.id)
          .single();

        if (error) {
          logger.warn('StoreSettingsContext', 'Erro ao carregar dados do tenant do Supabase (utilizando fallback local):', error.message);
          return;
        }

        if (data) {
          const loaded: StoreSettings = {
            storeName: data.name || storeSettings.storeName || 'Ótica Diana',
            attendantName: data.attendant_name || storeSettings.attendantName || 'Diana',
            cnpj: data.cnpj || storeSettings.cnpj || '',
            phone: data.phone || storeSettings.phone || '',
            email: data.email || storeSettings.email || '',
            addressStreet: data.address_street || storeSettings.addressStreet || '',
            addressNumber: data.address_number || storeSettings.addressNumber || '',
            addressComplement: data.address_complement || storeSettings.addressComplement || '',
            addressNeighborhood: data.address_neighborhood || storeSettings.addressNeighborhood || '',
            addressCity: data.address_city || storeSettings.addressCity || '',
            addressState: data.address_state || storeSettings.addressState || 'SP',
            addressZipCode: data.address_zip_code || storeSettings.addressZipCode || '',
            logoUrl: data.logo_url || storeSettings.logoUrl || '',
            privacyPin: (data as any).privacy_pin || storeSettings.privacyPin || '1234',
          };

          setStoreSettings(loaded);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded));
        }
      } catch (err) {
        logger.error('StoreSettingsContext', 'Exceção ao buscar dados da ótica no Supabase', err);
      }
    };

    fetchSupabaseSettings();
  }, [tenant?.id]);

  // Upload de Logo para o Supabase Storage Bucket 'store-assets'
  const uploadLogo = async (file: File): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      if (!tenant?.id || tenant.id === 'demo-tenant-id') {
        // Modo demo / local: converte para Data URL
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ success: true, url: reader.result as string });
          reader.onerror = () => resolve({ success: false, error: 'Erro ao processar imagem local.' });
          reader.readAsDataURL(file);
        });
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${tenant.id}-logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('store-assets')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        // Fallback para Base64 se o bucket não estiver criado ainda
        logger.warn('StoreSettingsContext', 'Upload no storage falhou, usando fallback em Base64:', uploadError.message);
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ success: true, url: reader.result as string });
          reader.readAsDataURL(file);
        });
      }

      const { data: publicUrlData } = supabase.storage
        .from('store-assets')
        .getPublicUrl(filePath);

      return { success: true, url: publicUrlData.publicUrl };
    } catch (err: any) {
      logger.error('StoreSettingsContext', 'Erro no upload de logo:', err);
      return { success: false, error: err?.message || 'Falha no upload.' };
    }
  };

  // Salva tanto no Supabase quanto no localStorage (garantindo persistência completa e suporte offline)
  const updateStoreSettings = async (newSettings: Partial<StoreSettings>) => {
    // Se o modo de valores ocultos estiver ativo, impede alterações nas configurações
    const isValuesHidden = localStorage.getItem('optisaas_values_visible') === 'false';
    if (isValuesHidden) {
      logger.warn('StoreSettingsContext', 'Tentativa de salvar configurações bloqueada pelo modo Valores Ocultos.');
      return;
    }

    setIsSaving(true);
    const updated = { ...storeSettings, ...newSettings };
    
    // Atualiza estado e cache local
    setStoreSettings(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error('Erro ao salvar no localStorage:', e);
    }

    // Se estiver conectado a um tenant real do Supabase, sincroniza na nuvem
    if (tenant?.id && tenant.id !== 'demo-tenant-id') {
      try {
        const { error } = await supabase
          .from('tenants')
          .update({
            name: updated.storeName,
            attendant_name: updated.attendantName,
            cnpj: updated.cnpj,
            phone: updated.phone,
            email: updated.email,
            address_street: updated.addressStreet,
            address_number: updated.addressNumber,
            address_complement: updated.addressComplement,
            address_neighborhood: updated.addressNeighborhood,
            address_city: updated.addressCity,
            address_state: updated.addressState,
            address_zip_code: updated.addressZipCode,
            logo_url: updated.logoUrl,
            privacy_pin: updated.privacyPin || '1234',
          })
          .eq('id', tenant.id);

        if (error) {
          logger.warn('StoreSettingsContext', 'Erro ao persistir tenant no Supabase (mantido no cache):', error.message);
        } else {
          logger.info('StoreSettingsContext', 'Configurações da loja persistidas com sucesso no Supabase!');
        }
      } catch (err) {
        logger.error('StoreSettingsContext', 'Exceção ao persistir no Supabase:', err);
      }
    }

    setIsSaving(false);
  };

  const openSettingsModal = () => setIsSettingsModalOpen(true);
  const closeSettingsModal = () => setIsSettingsModalOpen(false);

  return (
    <StoreSettingsContext.Provider
      value={{
        storeSettings,
        updateStoreSettings,
        uploadLogo,
        isSettingsModalOpen,
        openSettingsModal,
        closeSettingsModal,
        isSaving,
      }}
    >
      {children}
    </StoreSettingsContext.Provider>
  );
};

export const useStoreSettings = () => {
  const context = useContext(StoreSettingsContext);
  if (!context) {
    throw new Error('useStoreSettings must be used within a StoreSettingsProvider');
  }
  return context;
};
